/**
 * trace.tsx — the Trace: a navigable, editable, richly-rendered history.
 *
 * The trace is where trust is earned. It does not just show THAT things happened — it
 * lets a person narrow to what matters, follow a thread across the subjects an entry
 * touches, open any entry to its full underlying record (tool calls and, where present,
 * reasoning traces), edit any turn (human or AI) and choose how that edit is disclosed
 * to the model on replay, and read AI responses as formatted documents. None of this
 * weakens the boundary ladder: editing and replaying only ever PROPOSE.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useEventLog, useWorkspace } from "../lib/hooks.ts";
import { resolveRef } from "../../domain/resolve.ts";
import type { Workspace, AgentRun, AgentStep, DomainEvent, DisclosureMode } from "../../store/events.ts";
import { Markdown } from "../lib/markdown.tsx";
import { requestConsole } from "../lib/console-bus.ts";
import { editTurn } from "../lib/api.ts";
import { navigate } from "../lib/router.tsx";
import { useI18n, useT } from "../lib/i18n.tsx";
import {
  buildTraceRows,
  matchesFilter,
  composeReplay,
  latestEdit,
  EMPTY_FILTER,
  type TraceRow,
  type TraceActor,
  type TraceFilter,
} from "../lib/trace-model.ts";

const fmtTs = (ts: string): string => ts.slice(0, 19).replace("T", " ");

/** A resolved subject chip: click to follow that thread; ↗ to open its page. */
function SubjectChip({ ws, id, onThread }: { ws: Workspace; id: string; onThread: (id: string) => void }): JSX.Element {
  const ref = resolveRef(ws, id);
  return (
    <span className="subj-chip">
      <button type="button" className="subj-follow" onClick={() => onThread(id)} title={id}>
        {ref.label}
      </button>
      {ref.route ? (
        <button type="button" className="subj-go" aria-label={`open ${ref.label}`} onClick={() => navigate(ref.route!)}>
          ↗
        </button>
      ) : null}
    </span>
  );
}

/** The full record of one tool step — name, result, and its arguments (nothing hidden). */
function StepRow({ step }: { step: AgentStep }): JSX.Element {
  const glyph = step.kind === "tool_call" ? "→" : step.kind === "tool_result" ? (step.ok === false ? "✕" : "✓") : "⇢";
  const hasArgs = Boolean(step.args && typeof step.args === "object" && Object.keys(step.args as object).length > 0);
  return (
    <li className={`runstep ${step.kind}`}>
      <span className="runstep-kind">{glyph}</span>
      <span className="runstep-name">{step.name ?? step.summary}</span>
      {step.name && step.summary ? <span className="runstep-sum">{step.summary}</span> : null}
      {hasArgs ? <code className="runstep-args">{JSON.stringify(step.args)}</code> : null}
    </li>
  );
}

const DISCLOSURE_OPTS: readonly DisclosureMode[] = ["full", "final-only", "flagged"];

/** Inline editor for one turn. Human prompt edits re-ask; AI edits pick a disclosure mode. */
function TurnEditor({
  run,
  turn,
  current,
  onClose,
}: {
  run: AgentRun;
  turn: "prompt" | "response";
  current: string;
  onClose: () => void;
}): JSX.Element {
  const t = useT();
  const { locale } = useI18n();
  const [revised, setRevised] = useState(current);
  const [disclosure, setDisclosure] = useState<DisclosureMode>("full");
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState(false);
  const isResponse = turn === "response";

  const record = async (): Promise<boolean> => {
    setBusy(true);
    const ok = await editTurn({
      runId: run.runId,
      turn,
      revised,
      disclosure: isResponse ? disclosure : "final-only",
      ...(intent.trim() ? { intent: intent.trim() } : {}),
    });
    setBusy(false);
    return ok;
  };

  return (
    <div className="turn-editor">
      <textarea className="field-input turn-edit-text" rows={isResponse ? 4 : 2} value={revised} onChange={(e) => setRevised(e.target.value)} />
      {isResponse ? (
        <div className="turn-edit-controls">
          <label className="turn-disclosure">
            <span className="field-label">{t("trace.disclosure")}</span>
            <select className="field-input" value={disclosure} onChange={(e) => setDisclosure(e.target.value as DisclosureMode)}>
              {DISCLOSURE_OPTS.map((d) => (
                <option key={d} value={d}>
                  {t(`trace.disclosure.${d}`)}
                </option>
              ))}
            </select>
          </label>
          <input className="field-input" placeholder={t("trace.intent.ph")} value={intent} onChange={(e) => setIntent(e.target.value)} />
        </div>
      ) : null}
      <div className="turn-edit-actions">
        <button
          className="btn ghost sm"
          disabled={busy || !revised.trim() || revised === current}
          onClick={async () => {
            if (await record()) onClose();
          }}
        >
          {t("trace.edit.save")}
        </button>
        <button
          className="btn primary sm"
          disabled={busy || !revised.trim()}
          onClick={async () => {
            const ok = await record();
            if (ok) {
              const edit = { turn, original: current, revised, disclosure: isResponse ? disclosure : ("final-only" as DisclosureMode), ...(intent.trim() ? { intent: intent.trim() } : {}), editor: "ui-user", at: "" };
              requestConsole(composeReplay(run, edit, locale));
              onClose();
            }
          }}
        >
          {t("trace.edit.replay")}
        </button>
        <button className="btn ghost sm" disabled={busy} onClick={onClose}>
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}

/** The expanded body of an Assistant run: steps, reasoning, document, edits. */
function RunBody({ ws, run, onThread }: { ws: Workspace; run: AgentRun; onThread: (id: string) => void }): JSX.Element {
  const t = useT();
  const [editing, setEditing] = useState<"prompt" | "response" | null>(null);
  const [showReasoning, setShowReasoning] = useState(false);
  const promptEdit = latestEdit(run, "prompt");
  const responseEdit = latestEdit(run, "response");
  const subjects = [...new Set(run.steps.flatMap((s) => collectStepUrns(s)))];

  return (
    <div className="runcard-body">
      <div className="runcard-sub">
        {run.provider} · {run.model} · {fmtTs(run.createdAt)}
      </div>

      {/* Human turn */}
      <div className="turn">
        <div className="turn-head">
          <span className="pill ok">{t("trace.turn.human")}</span>
          <span className="turn-label">{t("trace.turn.prompt")}</span>
          <button className="btn ghost sm turn-edit-btn" onClick={() => setEditing(editing === "prompt" ? null : "prompt")}>
            ✎ {t("trace.edit")}
          </button>
        </div>
        <div className="turn-value">{promptEdit ? promptEdit.revised : run.prompt}</div>
        {promptEdit ? <div className="turn-changed">✎ {t("trace.humanChanged")}</div> : null}
        {editing === "prompt" ? <TurnEditor run={run} turn="prompt" current={promptEdit ? promptEdit.revised : run.prompt} onClose={() => setEditing(null)} /> : null}
      </div>

      {/* Tool steps — the full record */}
      {run.steps.length > 0 ? (
        <>
          <div className="foot-k">{t("trace.steps")}</div>
          <ol className="runsteps">
            {run.steps.map((s, i) => (
              <StepRow key={i} step={s} />
            ))}
          </ol>
        </>
      ) : null}

      {/* Reasoning trace, where present */}
      {run.reasoning ? (
        <div className="reasoning">
          <button className="reasoning-toggle" onClick={() => setShowReasoning((v) => !v)} aria-expanded={showReasoning}>
            <span className="runstep-kind">✺</span> {t("trace.reasoning")} <span className="runcard-caret">{showReasoning ? "▾" : "▸"}</span>
          </button>
          {showReasoning ? <div className="reasoning-body">{run.reasoning}</div> : null}
        </div>
      ) : null}

      {/* AI turn — rendered as a document */}
      {run.text ? (
        <div className="turn">
          <div className="turn-head">
            <span className="pill trace">{t("trace.turn.ai")}</span>
            <span className="turn-label">{t("trace.turn.response")}</span>
            {responseEdit ? <span className="turn-changed inline">✎ {t("trace.humanChanged")}</span> : null}
            <button className="btn ghost sm turn-edit-btn" onClick={() => setEditing(editing === "response" ? null : "response")}>
              ✎ {t("trace.edit")}
            </button>
          </div>
          <Markdown source={responseEdit ? responseEdit.revised : run.text} className="turn-doc" />
          {editing === "response" ? <TurnEditor run={run} turn="response" current={responseEdit ? responseEdit.revised : run.text} onClose={() => setEditing(null)} /> : null}
        </div>
      ) : null}

      {run.error ? <div className="runcard-error">{run.error}</div> : null}

      {/* Edit provenance — honest by construction */}
      {run.edits.length > 0 ? (
        <div className="edit-log">
          <div className="foot-k">{t("trace.editLog")}</div>
          {run.edits.map((e, i) => (
            <div className="edit-entry" key={i}>
              <span className="edit-meta">
                {e.turn === "prompt" ? t("trace.turn.prompt") : t("trace.turn.response")} · {t(`trace.disclosure.${e.disclosure}`)} · {e.editor}
              </span>
              {e.intent ? <span className="edit-intent">“{e.intent}”</span> : null}
              <div className="edit-diff">
                <span className="edit-from">− {e.original}</span>
                <span className="edit-to">+ {e.revised}</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Subjects — the threads this run touches */}
      {subjects.length > 0 ? (
        <div className="row-subjects">
          {subjects.map((id) => (
            <SubjectChip key={id} ws={ws} id={id} onThread={onThread} />
          ))}
        </div>
      ) : null}

      <div className="runcard-actions">
        <button className="btn ghost sm" onClick={() => requestConsole(promptEdit ? promptEdit.revised : run.prompt)}>
          ↻ {t("trace.replay")}
        </button>
      </div>
    </div>
  );
}

/** URNs inside one step's args/summary (kept local to avoid importing the whole model). */
function collectStepUrns(step: AgentStep): string[] {
  const out: string[] = [];
  const walk = (v: unknown): void => {
    if (typeof v === "string") {
      if (/^urn:[a-z0-9-]+:/i.test(v)) out.push(v);
    } else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === "object") Object.values(v).forEach(walk);
  };
  walk(step.args);
  if (step.summary) walk(step.summary);
  return out;
}

/** The expanded body of a raw domain event: full payload, nothing summarized away. */
function EventBody({ ws, event, onThread, subjects }: { ws: Workspace; event: DomainEvent; onThread: (id: string) => void; subjects: readonly string[] }): JSX.Element {
  const t = useT();
  return (
    <div className="runcard-body">
      <div className="runcard-sub">
        #{event.seq} · {event.actor.type}:{event.actor.id} · {fmtTs(event.ts)}
      </div>
      {subjects.length > 0 ? (
        <div className="row-subjects">
          {subjects.map((id) => (
            <SubjectChip key={id} ws={ws} id={id} onThread={onThread} />
          ))}
        </div>
      ) : null}
      <div className="foot-k">{t("trace.payload")}</div>
      <pre className="md-pre event-payload">
        <code>{JSON.stringify(event.payload, null, 2)}</code>
      </pre>
    </div>
  );
}

function RowCard({
  ws,
  row,
  open,
  highlighted,
  tabIndex,
  onToggle,
  onFocus,
  onThread,
}: {
  ws: Workspace;
  row: TraceRow;
  open: boolean;
  highlighted: boolean;
  tabIndex: number;
  onToggle: () => void;
  onFocus: () => void;
  onThread: (id: string) => void;
}): JSX.Element {
  const status = row.run?.status;
  return (
    <div className={`runcard ${status ?? ""} ${highlighted ? "hi" : ""}`} role="option" aria-selected={highlighted}>
      <button className="runcard-head" tabIndex={tabIndex} aria-expanded={open} onClick={onToggle} onFocus={onFocus}>
        {row.run ? (
          <span className={`pill run-${row.run.status}`}>{row.run.status}</span>
        ) : (
          <span className={`pill ${row.actor === "agent" ? "trace" : row.actor === "human" ? "ok" : ""}`}>{row.actor}</span>
        )}
        <span className="runcard-prompt">{row.title}</span>
        <span className="runcard-type">{row.typeLabel}</span>
        {row.subjects.length > 0 ? <span className="runcard-meta">◈ {row.subjects.length}</span> : null}
        <span className="runcard-caret" aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>
      {open
        ? row.run
          ? <RunBody ws={ws} run={row.run} onThread={onThread} />
          : row.event
            ? <EventBody ws={ws} event={row.event} subjects={row.subjects} onThread={onThread} />
            : null
        : null}
    </div>
  );
}

const ACTOR_FILTERS: readonly (TraceActor | "all")[] = ["all", "human", "agent", "system"];

export function TraceRoute(): JSX.Element {
  const { ws } = useWorkspace();
  const events = useEventLog();
  const t = useT();
  const [filter, setFilter] = useState<TraceFilter>(EMPTY_FILTER);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [highlight, setHighlight] = useState(0);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const rows = useMemo(() => (ws ? buildTraceRows(ws, events) : []), [ws, events]);
  const visible = useMemo(() => rows.filter((r) => matchesFilter(r, filter)), [rows, filter]);

  useEffect(() => setHighlight(0), [filter]);

  // Roving focus: keep the highlighted row's header focused, like the query result list.
  useEffect(() => {
    const heads = listRef.current?.querySelectorAll<HTMLElement>(".runcard-head");
    const el = heads?.[Math.min(highlight, (heads?.length ?? 1) - 1)];
    if (el && document.activeElement && listRef.current?.contains(document.activeElement)) {
      el.focus();
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, visible.length]);

  const toggle = (key: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onListKey = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(visible.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (highlight === 0) searchRef.current?.focus();
      else setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(visible.length - 1);
    }
    // Enter / Space toggle via the row header button's own activation — handling them
    // here too would fire the toggle twice.
  };

  if (!ws) return <div className="wrap"><p className="mini">{t("common.loading")}</p></div>;

  const threadRef = filter.thread ? resolveRef(ws, filter.thread) : null;

  return (
    <div className="wrap">
      <div className="page-head">
        <div>
          <div className="sec-no">control plane</div>
          <h1 className="page-title">{t("trace.title")}</h1>
          <p className="page-desc">{t("trace.desc")}</p>
        </div>
      </div>

      {/* Filter bar — calm by default, depth on demand */}
      <div className="trace-filter">
        <div className="trace-search">
          <span className="gly" style={{ color: "var(--faint)" }}>⌕</span>
          <input
            ref={searchRef}
            className="palette-search"
            placeholder={t("trace.search")}
            value={filter.text}
            onChange={(e) => setFilter((f) => ({ ...f, text: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight(0);
                listRef.current?.querySelector<HTMLElement>(".runcard-head")?.focus();
              }
            }}
          />
        </div>
        <div className="trace-actors">
          {ACTOR_FILTERS.map((a) => (
            <button key={a} className={`trace-actor ${filter.actor === a ? "on" : ""}`} onClick={() => setFilter((f) => ({ ...f, actor: a }))}>
              {t(`trace.actor.${a}`)}
            </button>
          ))}
        </div>
        {threadRef ? (
          <button className="trace-thread" onClick={() => setFilter((f) => ({ ...f, thread: null }))} title={filter.thread ?? ""}>
            {t("trace.thread")}: {threadRef.label} <span aria-hidden="true">✕</span>
          </button>
        ) : null}
        <span className="trace-count">{t("trace.count", { n: visible.length, total: rows.length })}</span>
      </div>

      {visible.length === 0 ? (
        <div className="empty-note">{rows.length === 0 ? t("trace.empty") : t("trace.noMatches")}</div>
      ) : (
        <div className="trace-list" ref={listRef} role="listbox" aria-label={t("trace.title")} onKeyDown={onListKey}>
          {visible.map((row, i) => (
            <RowCard
              key={row.key}
              ws={ws}
              row={row}
              open={open.has(row.key)}
              highlighted={i === highlight}
              tabIndex={i === highlight ? 0 : -1}
              onToggle={() => toggle(row.key)}
              onFocus={() => setHighlight(i)}
              onThread={(id) => setFilter((f) => ({ ...f, thread: id }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
